<template>
  <div ref="page" class="container">
    <div ref="overlay" class="overlay">
      <div class="overlay__main overlay__main--base" :class="{ 'overlay__main--hiding': showForm }">
        <Notification :message="notification" :trigger-notification="showNotification" @toggle-visibility="hideToast" />
        <div class="content">
          <EmptyAvailability v-if="!hasAvailabilities" @click="openForm" />
          <AvailabilityList
            v-if="hasAvailabilities"
            ref="availabilityList"
            :availabilities="viewer.availabilities"
            :selected-availability="selectedAvailability"
            @scroll-end="showMore"
            @availability-selected="openForm"
          />
        </div>

        <div class="action-bar" :class="buttonClass">
          <div class="container">
            <div class="action-bar__inner">
              <div class="action-bar__mobile-action">
                <AddButton ref="button" @click="openForm" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <aside ref="overlayAside" class="overlay__aside overlay__aside--base" :class="{ 'overlay__aside--active': showForm }">
        <div v-if="!showForm && hasAvailabilities" class="overlay__aside-placeholder">
          <div class="heading-bar">
            <h1 class="hdg hdg--2">Availability calendar</h1>
          </div>
          <p class="overlay__aside-copy">
            Now, families can send you interview requests when you're marked as available. You can also
            <a href="/provider/jobs/search" class="o-link--primary">apply to jobs</a> directly.
          </p>
          <div class="vert-list--sm">
            <p><strong>And remember:</strong></p>
            <ul class="vert-list--bullet">
              <li>Update your calendar when things come up (like holidays or vacations!)</li>
              <li>Delete or edit availability by clicking on specific time blocks in the list</li>
              <li>See your full calendar of availability in your profile</li>
            </ul>
          </div>
        </div>
        <AvailabilityForm
          v-if="showForm"
          ref="availabilityForm"
          :key="selectedAvailability?.node?.start ?? 'new'"
          :availability="selectedAvailability"
          :form-class="formClass"
          @clear="clearAvailability"
          @submit="createAvailability"
          @submit-repeating="createRepeatingAvailability"
          @update="updateAvailability"
          @close="closeForm"
        />
      </aside>
    </div>
  </div>
</template>

<script lang="ts">
import { formatISO, parseISO } from 'date-fns';
import debounce from 'lodash/debounce';
import { CREATE_AVAILABILITY, CREATE_REPEATING_AVAILABILITY, CLEAR_AVAILABILITY } from '../../graphql/availability/mutations';
import { VIEWER_AVAILABILITIES_QUERY } from '../../graphql/availability/queries';
import EmptyAvailability from './EmptyAvailability.vue';
import AvailabilityList from './AvailabilityList.vue';
import AddButton from './AddButton.vue';
import AvailabilityForm from './AvailabilityForm.vue';
import Notification from '../../components/notification/Notification.vue';
import { drill } from '../../lib/objectTools';
import { sendSegmentEvent } from '../../lib/segment';

const AVAILABLE_KIND = 'AVAILABLE';
const FAST_TIMEOUT = 15;

export default {
  name: 'Availability',
  components: {
    EmptyAvailability,
    AvailabilityList,
    AddButton,
    AvailabilityForm,
    Notification,
  },
  data(): any {
    return {
      viewer: null,
      viewEventSent: false,
      showForm: false,
      selectedAvailability: null,
      notification: null,
      showNotification: false,
      debouncedScroll: null,
      buttonClass: '',
      formClass: '',
      savedScroll: null,
      viewerLoadingKey: 0,
      cachedIsMobile: null,
    };
  },
  computed: {
    hasAvailabilities(): boolean {
      const edges = drill(this.viewer, ['availabilities', 'edges']);
      return edges && edges.length > 0;
    },
    lastAvailability(): string | null {
      const edges = drill(this.viewer, ['availabilities', 'edges']);
      if (edges && edges.length !== 0) return edges[edges.length - 1].cursor;
      return null;
    },
    hasMore(): boolean {
      return drill(this.viewer, ['availabilities', 'pageInfo', 'hasNextPage']);
    },
    isMobile(): boolean {
      if (this.cachedIsMobile === null) {
        const r = this.$refs.overlay.getBoundingClientRect();
        this.cachedIsMobile = r.width < 640;
      }
      return this.cachedIsMobile;
    },
  },
  apollo: {
    viewer: {
      query: VIEWER_AVAILABILITIES_QUERY,
      result() {
        this.trackViewAvailability();
        setTimeout(() => {
          this.adjustPositions();
        }, 100);
      },
      loadingKey: 'viewerLoadingKey',
    },
  },
  mounted() {
    this.debouncedScroll = debounce(this.adjustPositions, FAST_TIMEOUT);
    window.addEventListener('scroll', this.debouncedScroll);
    window.addEventListener('resize', this.invalidateMobileCache);
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.debouncedScroll);
    window.removeEventListener('resize', this.invalidateMobileCache);
  },
  methods: {
    invalidateMobileCache() {
      this.cachedIsMobile = null;
    },
    mergeResults(previousResult, { fetchMoreResult }): any {
      const e1 = drill(previousResult, ['viewer', 'availabilities', 'edges']);
      const e2 = drill(fetchMoreResult, ['viewer', 'availabilities', 'edges']);
      if (e1 && e2) {
        const combined = JSON.parse(JSON.stringify(fetchMoreResult)); // clone
        combined.viewer.availabilities.edges = e1.concat(e2);
        return combined;
      }
      return previousResult;
    },
    showMore() {
      if (this.hasMore && this.viewerLoadingKey === 0) {
        this.$apollo.queries.viewer.fetchMore({
          variables: { after: this.lastAvailability },
          updateQuery: this.mergeResults,
        });
      }
    },
    openForm(availability: any) {
      this.rememberScroll();
      this.selectedAvailability = availability?.node ? availability : null;
      this.showForm = true;
      setTimeout(() => {
        if (this.isMobile) this.scrollToTop();
        else this.adjustPositions();
      }, FAST_TIMEOUT);
    },
    createAvailability(start: Date, end: Date) {
      this.trackSubmitAvailability('add', start, end);
      this.$apollo
        .mutate({
          mutation: CREATE_AVAILABILITY,
          variables: {
            start: formatISO(start),
            end: formatISO(end),
            kind: AVAILABLE_KIND,
          },
        })
        .then(async () => {
          await this.$apollo.queries.viewer.refetch();
          this.closeForm();
          this.notify('Availability added successfully');
        })
        .catch((error) => {
          console.error(error);
          this.notify("Availability can't be created. Try again.");
        });
    },
    createRepeatingAvailability(start: Date, end: Date, schedule: string) {
      this.trackSubmitAvailability('add', start, end, { input: 'repeating' });
      this.$apollo
        .mutate({
          mutation: CREATE_REPEATING_AVAILABILITY,
          variables: {
            start: formatISO(start),
            end: formatISO(end),
            schedule,
          },
        })
        .then(async () => {
          await this.$apollo.queries.viewer.refetch();
          this.closeForm();
          this.notify('Availability added successfully');
        })
        .catch((error) => {
          console.error(error);
          this.notify("Availability can't be created. Try again.");
        });
    },
    clearAvailability(availability: any) {
      const start = parseISO(availability.node.start);
      const end = parseISO(availability.node.end);
      this.trackSubmitAvailability('delete', start, end);
      this.$apollo
        .mutate({
          mutation: CLEAR_AVAILABILITY,
          variables: {
            start: formatISO(start),
            end: formatISO(end),
          },
        })
        .then(async () => {
          await this.$apollo.queries.viewer.refetch();
          this.closeForm();
          this.notify('Availability deleted successfully');
        })
        .catch((error) => {
          console.error(error);
          this.notify("Availability can't be deleted. Try again.");
        });
    },
    async updateAvailability(clearBounds: { clearStart: string; clearEnd: string }, start: Date, end: Date) {
      this.trackSubmitAvailability('edit', start, end);
      // Temporary mitigation for a backend bug (see ticket): the combined clear+create edit
      // corrupts the saved block whenever the cleared (old) range overlaps the new range, because
      // the clear carves its slice out of the just-created block. To avoid that, we create the new
      // block first and then clear only the leftover slivers of the old range that the new range
      // does not cover, so a clear never touches the range the user is keeping.
      try {
        await this.$apollo.mutate({
          mutation: CREATE_AVAILABILITY,
          variables: {
            start: formatISO(start),
            end: formatISO(end),
            kind: AVAILABLE_KIND,
          },
        });

        const leftoverRanges = this.subtractRange(parseISO(clearBounds.clearStart), parseISO(clearBounds.clearEnd), start, end);
        for (const range of leftoverRanges) {
          await this.$apollo.mutate({
            mutation: CLEAR_AVAILABILITY,
            variables: {
              start: formatISO(range.start),
              end: formatISO(range.end),
            },
          });
        }

        await this.$apollo.queries.viewer.refetch();
        this.closeForm();
        this.notify('Availability edit saved.');
      } catch (error) {
        console.error(error);
        this.notify("Availability can't be edited. Try again.");
      }
    },
    subtractRange(oldStart: Date, oldEnd: Date, newStart: Date, newEnd: Date): Array<{ start: Date; end: Date }> {
      const ranges: Array<{ start: Date; end: Date }> = [];
      if (oldStart < newStart) {
        ranges.push({ start: oldStart, end: oldEnd < newStart ? oldEnd : newStart });
      }
      if (oldEnd > newEnd) {
        ranges.push({ start: oldStart > newEnd ? oldStart : newEnd, end: oldEnd });
      }
      return ranges;
    },
    trackSubmitAvailability(action: string, start: Date, end: Date, options?: any) {
      const i = options && options.input ? options.input : 'manual';
      sendSegmentEvent('Availability Submitted', {
        start: formatISO(start),
        end: formatISO(end),
        type: 'confirmed',
        action,
        input: i,
      });
    },
    trackViewAvailability() {
      if (!this.viewEventSent) {
        sendSegmentEvent('Availability Viewed', {
          upcomingBlock: this.hasAvailabilities,
        });
        this.viewEventSent = true;
      }
    },
    scrollToTop() {
      if (this.isMobile) window.scrollTo(0, 0);
    },
    rememberScroll() {
      if (this.isMobile && document.body && document.documentElement) {
        this.savedScroll = document.body.scrollTop || document.documentElement.scrollTop;
      }
    },
    closeForm() {
      this.showForm = false;
      this.selectedAvailability = null;
      setTimeout(() => this.scrollPage(), FAST_TIMEOUT);
    },
    scrollPage() {
      if (this.savedScroll) {
        window.scrollTo(0, this.savedScroll);
        this.savedScroll = null;
      }
    },
    notify(message: string) {
      this.notification = message;
      this.showNotification = true;
    },
    hideToast() {
      this.showNotification = false;
    },
    adjustPositions() {
      const pageRect = this.$refs.page.getBoundingClientRect();
      const buttonRect = this.$refs.button.$el.getBoundingClientRect();
      const overlayRect = this.$refs.overlayAside.getBoundingClientRect();

      if (pageRect.bottom + buttonRect.height > window.innerHeight) {
        this.buttonClass = 'action-bar--fixed';
      } else if (pageRect.bottom < window.innerHeight) {
        this.buttonClass = 'action-bar--anchored';
      } else this.buttonClass = '';

      if (!this.showForm) {
        this.formClass = '';
        return;
      }

      const availabilityFormEl = this.$refs.availabilityForm?.$el;
      if (!availabilityFormEl) return;

      const availabilityFormRect = availabilityFormEl.getBoundingClientRect();
      const headerHeight = 80;
      const marginHeight = 50;

      if (pageRect.top >= headerHeight) {
        this.formClass = '';
      } else if (overlayRect.bottom < availabilityFormRect.height + headerHeight + marginHeight) {
        this.formClass = 'overlay__inner--anchored';
      } else {
        this.formClass = 'overlay__inner--fixed';
      }
    },
  },
};
</script>
